import React, { useState, useEffect, useRef, useCallback } from 'react';
import './CustomButton.css'; // Make sure this CSS exists

// Ensure buttonData defaults to an empty array if not provided
const MultipleButton = ({ onClick, children, buttonData = [], baseClasses = "custom-button circle", isDisabled = false }) => {
    const [pressedButtons, setPressedButtons] = useState(new Set());
    const buttonRefs = useRef({});
    // --- State Update Handler ---
    const handleButtonPress = useCallback((buttonId, isPressed) => {
        setPressedButtons(prev => {
            const newSet = new Set(prev);
            if (isPressed) newSet.add(buttonId);
            else newSet.delete(buttonId);
            return newSet;
        });
    }, []);

    // --- Touch Event Handling ---
    useEffect(() => {
        const handlers = {}; // Store handlers for proper removal
        const currentButtonRefs = buttonRefs.current;

        buttonData.forEach(button => {
            const buttonElement = currentButtonRefs[button.id];
            if (!buttonElement) {
                // Warning if ref is somehow not set before listeners are added
                console.warn(`Button element ref not found for id: ${button.id} during effect setup.`);
                return;
            };

            // --- Add validation for position data ---
            if (!button.position) {
                console.warn(`Button data for id: ${button.id} is missing the 'position' property. It will not be positioned absolutely.`);
            } else if (typeof button.position !== 'object' || button.position === null) {
                console.warn(`Button data 'position' for id: ${button.id} must be an object (e.g., { top: '10px', left: '20px' }). Received:`, button.position);
            }
            // --- End validation ---

            const handleTouchStart = (event) => {
                if (isDisabled)
                    return null;
                event.preventDefault(); // Prevent scroll/zoom on touch
                handleButtonPress(button.id, true);
                if (onClick) onClick((button.isImageInfoMode || button.isInfoMode) ? button.description : button.label);
            };
            const handleTouchEnd = () => {
                handleButtonPress(button.id, false);
                if (onClick && (button.isImageInfoMode || button.isInfoMode)) onClick();
            };

            // Store exact handler references for removal
            handlers[button.id] = {
                touchstart: handleTouchStart,
                touchend: handleTouchEnd,
                touchcancel: handleTouchEnd // Use same handler for cancel
            };

            // Add listeners with correct options
            buttonElement.addEventListener('touchstart', handleTouchStart, { passive: false });
            buttonElement.addEventListener('touchend', handleTouchEnd);
            buttonElement.addEventListener('touchcancel', handleTouchEnd);
        });

        // --- Cleanup Function ---
        return () => {
            buttonData.forEach(button => {
                const buttonElement = currentButtonRefs[button.id];
                const buttonHandlers = handlers[button.id]; // Retrieve stored handlers
                if (buttonElement && buttonHandlers) {
                    // Remove the specific listeners that were added
                    buttonElement.removeEventListener('touchstart', buttonHandlers.touchstart, { passive: false });
                    buttonElement.removeEventListener('touchend', buttonHandlers.touchend);
                    buttonElement.removeEventListener('touchcancel', buttonHandlers.touchcancel);
                }
            });
        };
    }, [isDisabled, buttonData, onClick, handleButtonPress]); // Dependencies for the effect

    const GetImageStyle = (button) => {
        return {
            transform: `rotateY(0deg) translateZ(${button.imgRadius}px)`,
            width: `${button.imgRadius}px`,
            height: `${button.imgRadius}px`,
            marginLeft: `-${button.imgRadius / 2}px`,
            marginTop: `-${button.imgRadius / 2}px`,
        };
    };

    const GetButtonStyle = (button, isPressed, isDisabled) => {
        // Image-based buttons get their own sizing in GetImageStyle, so we skip them here:
        if (button.image != null) return {};

        const style = {};
        if (!isDisabled) {
            style.backgroundColor = isPressed ? button.pressedColor : button.color
        }

        // **Apply radiusSize** to width & height
        if (button.radiusSize != null) {
            // allow either a number (px) or a string
            const sizeValue =
                typeof button.radiusSize === 'number'
                    ? `${button.radiusSize}px`
                    : button.radiusSize;

            style.width = sizeValue;
            style.height = sizeValue;
        }

        return style;
    };


    const GetButtonClassName = (button, isPressed) => {
        const GetColorClass = (button, isPressed) => {
            return `${baseClasses}
                ${isPressed ? 'translate-y-1 shadow-inner' : 'shadow-lg hover:shadow-xl'}
                ${button.shadowColor || ''}
                ${button.ringColor ? `ring-4 ring-opacity-50 ${isPressed ? button.ringColor : 'ring-transparent'}` : ''}
                ${button.activeClass && isPressed ? button.activeClass : ''}
                transition-all duration-100 ease-in-out
                select-none`;
        };

        return button.image != null ? null : GetColorClass(button, isPressed);
    };

    const GetWrapperStyle = (button) => {
        return button.image != null
            ? GetImageStyle(button)
            : {
                position: 'absolute',
                ...(button.position && typeof button.position === 'object'
                    ? button.position
                    : { top: 'auto', left: 'auto' })
            };
    };

    const GetOnClickImgStyle = (isImageInfoMode, isPressed) => {
        return {
            filter: isPressed ? 'brightness(0.5)' : isImageInfoMode ? 'grayscale(80%) brightness(1.2) contrast(1.1)' : 'brightness(1)',
            transition: 'filter 0.1s ease',
        };
    }


    // --- Rendering Logic ---
    return (
        <div className="relative w-full h-full">
            {buttonData.map(button => {
                const isPressed = pressedButtons.has(button.id);
                return (
                    <div className={button.image != null ? "carousel-item-circle" : {}}
                        key={button.id}
                        style={GetWrapperStyle(button)} // Apply the calculated position style
                    >
                        <button
                            ref={el => buttonRefs.current[button.id] = el} // Assign ref to the actual button

                            // --- Mouse Events ---
                            onMouseDown={isDisabled ? null : (e) => {
                                if (e.button === 0) { // Primary button only
                                    handleButtonPress(button.id, true);
                                    if (onClick) onClick((button.isImageInfoMode || button.isInfoMode) ? button.description : button.label);
                                }
                            }}
                            onMouseUp={isDisabled ? null : (e) => {
                                if (e.button === 0)
                                    handleButtonPress(button.id, (button.isImageInfoMode || button.isInfoMode) ? true : false);
                            }}
                            onMouseLeave={isDisabled ? null : (e) => {
                                if (button.isImageInfoMode || button.isInfoMode) {
                                    handleButtonPress(button.id, false);
                                    onClick();
                                }
                                else {
                                    if (e.buttons === 1 && isPressed)  // Check if primary button is still physically down
                                        handleButtonPress(button.id, false);
                                }
                            }}

                            // Touch events handled by the useEffect
                            className={isDisabled ? "custom-button-circle-disabled" : GetButtonClassName(button, isPressed)}
                            style={GetButtonStyle(button, isPressed, isDisabled)}
                            onContextMenu={isDisabled ? null : (e) => e.preventDefault()} // Prevent context menu on long touch
                            disabled={isDisabled}
                        >
                            {button.image && <img src={button.image} alt={button.label} style={GetOnClickImgStyle(button.isImageInfoMode, isPressed)} className={button.isImageInfoMode ? "image-circle-outlined" : null} />}
                            {button.image != null ? "" : children && children[button.id] ? children[button.id] : button.label}
                            {/* Ping Animation */}
                            {!isDisabled && isPressed && (<div className="absolute -inset-2 bg-white opacity-20 rounded-full animate-ping pointer-events-none" />)}
                        </button>
                    </div>
                );
            })}
        </div>);
};

export default MultipleButton;